import {
  Button,
  type ButtonProps,
  Field,
  FieldContent,
  FieldDescription,
  FieldDescriptionAndValidation,
  FieldLabel,
  type FieldProps,
  FieldValidation,
  Icon,
  Input,
  Progress,
  labelToString,
} from '@glade-ai/ui';
import { type ChangeEvent, type MouseEvent, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type PasswordValidation, validatePassword } from '@/helpers/validatePassword';

/** A form field for password input with visibility toggle and strength indicator. */
export const PasswordField = ({
  field: { onChange, ...field },
  fieldState,
  label = 'forms.inputs.password.label',
  placeholder = 'forms.inputs.password.placeholder',
  variant = 'signIn',
  autoComplete = 'current-password',
  onForgotPasswordClick,
  ...inputProps
}: FieldProps & { variant?: 'signUp' | 'signIn'; onForgotPasswordClick?: ButtonProps['onClick'] }) => {
  const { t } = useTranslation();
  const [passwordValidation, setPasswordValidation] = useState<PasswordValidation | null>(null);
  const [show, setShow] = useState(false);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setPasswordValidation(
        validatePassword((event.target as HTMLInputElement).value, { isRequired: variant === 'signUp' }),
      );
      onChange?.(event);
    },
    [variant, onChange],
  );

  const handleShowClick = useCallback((): void => {
    setShow((prevShow) => !prevShow);
  }, []);

  const handleForgotPasswordClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      onForgotPasswordClick?.(event);
    },
    [onForgotPasswordClick],
  );

  const displayHelpMessage =
    variant === 'signUp' && passwordValidation && passwordValidation.message !== fieldState.error?.message;

  return (
    <Field validationValence={fieldState.invalid ? 'error' : 'neutral'}>
      <FieldContent>
        {variant === 'signIn' && onForgotPasswordClick ? (
          <div role='none' className='flex items-center'>
            <FieldLabel className='grow'>{labelToString(t, label)}</FieldLabel>
            <Button type='button' variant='link' className='-my-1' onClick={handleForgotPasswordClick}>
              Forgot password
            </Button>
          </div>
        ) : (
          <FieldLabel>{labelToString(t, label)}</FieldLabel>
        )}
        <div role='none' className='relative'>
          <Input
            type={show ? 'text' : 'password'}
            {...inputProps}
            {...field}
            placeholder={labelToString(t, placeholder)}
            onChange={handleChange}
            autoComplete={autoComplete}
            data-rr-is-password
          />
          <Button
            type='button'
            variant='ghost'
            onClick={handleShowClick}
            className='absolute inset-y-1 end-1 !px-2 py-1 h-auto'
          >
            <span className='sr-only'>{t(show ? 'forms.inputs.password.hide' : 'forms.inputs.password.show')}</span>
            <Icon icon={show ? 'ph--eye--regular' : 'ph--eye-slash--regular'} />
          </Button>
        </div>
        {variant === 'signUp' && <Progress value={passwordValidation?.progress ?? 0} />}
        <FieldDescriptionAndValidation className={variant === 'signUp' ? '-mt-1' : undefined}>
          {displayHelpMessage && <FieldDescription>{passwordValidation.message}</FieldDescription>}
          {displayHelpMessage && fieldState.invalid && ' '}
          {fieldState.invalid && <FieldValidation>{fieldState.error?.message}</FieldValidation>}
        </FieldDescriptionAndValidation>
      </FieldContent>
    </Field>
  );
};
